import React, { useState, useRef, useEffect, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type RowClassParams, type IRowNode, type CellValueChangedEvent, type SelectionChangedEvent, type RowSelectionOptions } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import gridInputCellEditor from "@/components/aggrid/aggrid_input_cell_editor";
import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./popup_backup_stock_info.scoped.scss";

interface State {
  gridApi: GridApi | null,
  currIndex: number,
  inoutType: string,
  skuStockInfos: any[],
  remainQty: string,
  jobQty: string
}

const defState: State = {
  gridApi: null,
  currIndex: 0,
  inoutType: "",
  skuStockInfos: [],
  remainQty: "0",
  jobQty: "0"
};

const validationMsg = [
  {id: "workReqEtcNo", type: "InvalidStatus", msg: "작업대기 상태만 작업지시할 수 있습니다."},
  {id: "workInfos", type: "NotFound", msg: "선택SKU가 있는 작업만 작업지시할 수 있습니다."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."}
];

const defColDef = { ...kutil.defaultColDef, cellDataType: false };

function PopupBackupStockInfo(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const gridStateKey = "/inoutbound/popup_backup_stock_info";
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey), []);

  const isAutoSelect = useRef(false);
  const remainQtyRef = useRef("0");
  const inoutTypeRef = useRef("");

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Location", field: "locationId", width: 120, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.locationId },
    { headerName: "SKU No", field: "skuNoEx", width: 100, cellStyle: {textAlign: "center"} },
    { headerName: "창고", field: "wh", width: 100, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 150, cellStyle: {textAlign: "left"} },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 120, cellStyle: {textAlign: "left"} },
    { headerName: "Stock Qty", headerClass: "hd-ta-right", field: "stockQty", width: 110, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number },
    { headerName: "UOM", field: "uom", width: 80, cellStyle: {textAlign: "center"} },
    { headerName: "Job Qty", headerClass: "hd-ta-right", field: "jobQty", width: 110, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number,
      cellClass: (p: any) => (inoutTypeRef.current !== "I" && p.data.outPossYn == "Y") ? "input-cell ta-right" : "",
      editable: (p: any) => inoutTypeRef.current !== "I" && p.data.outPossYn == "Y",
      singleClickEdit:true,
      cellEditor: gridInputCellEditor,
      cellEditorParams: { maxLength: 7 }
    },
    { headerName: "작업중", field: "workStationId", width: 100, cellStyle: {textAlign: "center"},
      valueGetter: (p: any) => {
        if (p.data.outPossYn == "N") {
          return (p.data.workStationId ? commonCode.getCodeValue("workStationId", p.data.workStationId) : "금지CELL");
        }
      }
    }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    getRowStyle: (p: RowClassParams) => {
      if (p.data.outPossYn == "N") {
        return {"backgroundColor": "#FFCBCB"};
      }
    },
    selectionColumnDef: {
      resizable: true
    },
    stopEditingWhenCellsLoseFocus: true,
  }), []);
  const rowSelection = useMemo((): RowSelectionOptions => ({
    mode: state.inoutType === "I" ? "singleRow" : "multiRow",
    isRowSelectable: (n: IRowNode) => n.data.outPossYn == "Y",
    hideDisabledCheckboxes: true
  }), [state.inoutType]);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey);
    },
    onRowDataUpdated: (e: any) => {
      autoSelectRows(e.api);
    },
    onCellValueChanged: (e: CellValueChangedEvent) => {
      if (e.colDef.field === "jobQty") {
        e.node.setSelected(!!e.newValue && Number(e.newValue) > 0);
        updateJobQty(e.api);
      }
    },
    onSelectionChanged: (e: SelectionChangedEvent) => {
      if (isAutoSelect.current) return;

      const isIn = inoutTypeRef.current === "I";
      e.api.forEachNode((node) => {
        if (!node.isSelected()) {
          if (node.data.jobQty) node.setData({ ...node.data, jobQty: "" });
        } else if (isIn && !Number(node.data.jobQty)) {
          node.setData({ ...node.data, jobQty: remainQtyRef.current });
        }
      });
      updateJobQty(e.api);
    },
  });

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
  }

  async function searchInfos(index = 0) {
    const currInfo = props.data.infos[index];
    const remainQty = currInfo.remainQty ?? currInfo.reqQty;
    remainQtyRef.current = remainQty;
    inoutTypeRef.current = currInfo.inoutType;
    setState(prev => ({ ...prev, currIndex: index, inoutType: currInfo.inoutType, skuStockInfos: currInfo.skuStockInfos, remainQty: remainQty, jobQty: "0" }));
  }

  async function closePopup() {
    if (props.data.multiYn == "N") {
      if (await kutil.confirm("취소하시겠습니까?") == false) {
        return false;
      }
      props.onClose();
    } else {
      const nextIndex = state.currIndex + 1;
      if (nextIndex < props.data.infos.length) {
        await searchInfos(nextIndex);
      } else {
        props.onClose({ isSuccess: true });
      }
    }
  }

  function updateJobQty(api: GridApi) {
    const total = api.getSelectedNodes().reduce((sum, node) => sum + (Number(node.data.jobQty) || 0), 0);
    setState(prev => ({ ...prev, jobQty: String(total) }));
  }
  function autoSelectRows(api: GridApi) {
    const isIn = inoutTypeRef.current === "I";
    let remaining = Number(remainQtyRef.current) || 0;
    const targets: IRowNode[] = [];

    isAutoSelect.current = true;
    api.deselectAll();
    api.forEachNode((node) => {
      if (node.data.outPossYn !== "Y" || remaining <= 0) {
        node.setData({ ...node.data, jobQty: "" });
        return;
      }
      const qty = isIn ? remaining : Math.min(Number(node.data.stockQty) || 0, remaining);
      if (qty <= 0) {
        node.setData({ ...node.data, jobQty: "" });
        return;
      }
      remaining -= qty;
      node.setData({ ...node.data, jobQty: qty });
      targets.push(node);
    });
    api.setNodesSelected({ nodes: targets, newValue: true });
    isAutoSelect.current = false;

    updateJobQty(api);
  }

  async function save() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("SKU를 선택해 주세요.");
      return;
    }

    for (const info of selectedInfos) {
      if (!info.jobQty || Number(info.jobQty) < 1) {
        kutil.alert("작업수량을 입력해 주세요.");
        return;
      } else {
        if (state.inoutType == "O" && Number(info.jobQty) > Number(info.stockQty)) {
          kutil.alert("재고수량을 초과하였습니다.");
          return;
        }
      }
    }

    if (Number(state.jobQty) > Number(state.remainQty)) {
      kutil.alert("지시수량을 초과하였습니다.");
      return;
    }

    if (props.data.multiYn == "N") {
      if (await kutil.confirm("작업지시하시겠습니까?") == false) {
        return false;
      }
    }

    const currInfo = props.data.infos[state.currIndex];
    let newInfo: any = {};
    try {
      currInfo.choiceSkuType = "M";
      currInfo.choiceSkuCallQty = selectedInfos.length;
      currInfo.choiceSkuStockQty = currInfo.skuStockInfos.length;
      const data = await kutil.sendPost("/api/inoutbound/backup_work.proc?mode=orderWork", { ...currInfo, workInfos: selectedInfos }, {
        validationMsg: validationMsg
      });
      newInfo = data.info;
    } catch(e) {
      console.log(e);
      return;
    }

    if (props.data.multiYn == "N") {
      await kutil.alert("작업지시하였습니다.");
      props.onClose({ isSuccess: true, info: newInfo });
    } else {
      const nextIndex = state.currIndex + 1;
      if (nextIndex < props.data.infos.length) {
        await searchInfos(nextIndex);
      } else {
        await kutil.alert("작업지시하였습니다.");
        props.onClose({ isSuccess: true });
      }
    }
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "1400px", }, }, }}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          재고정보
        </DialogTitle>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* 상단 정보 */}
            <div>
              <div>
                <p>지시수량</p>
                <Input
                  inputProps={{ readOnly: true }}
                  value={valueFormatter.number(state.remainQty)}
                />
              </div>
              <div>
                <p>작업수량</p>
                <Input
                  inputProps={{ readOnly: true }}
                  value={valueFormatter.number(state.jobQty)}
                />
              </div>
            </div>
            {/* 그리드 */}
            <AgGridProvider modules={modules}>
              <div className="grid-area">
                <CustomAgGridReact
                  columnDefs={gridInfo}
                  rowData={state.skuStockInfos}
                  defaultColDef={defColDef}
                  initialState={gridState}
                  getRowStyle={gridOptions.getRowStyle}
                  rowSelection={rowSelection}
                  selectionColumnDef={gridOptions.selectionColumnDef}
                  stopEditingWhenCellsLoseFocus={gridOptions.stopEditingWhenCellsLoseFocus}
                  onGridReady={gridCallbacks.onGridReady}
                  onStateUpdated={gridCallbacks.onStateUpdated}
                  onRowDataUpdated={gridCallbacks.onRowDataUpdated}
                  onCellValueChanged={gridCallbacks.onCellValueChanged}
                  onSelectionChanged={gridCallbacks.onSelectionChanged}
                />
              </div>
            </AgGridProvider>
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            취소
          </Button>
          { auth.write &&
          <Button variant="contained" autoFocus onClick={save}>
            확인
          </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupBackupStockInfo);
